/**
 * Lightweight SDK logger.
 *
 * Debug output is suppressed in production. Enable verbose logging by calling
 * SDKLogger.setDebug(true) before initialising the SDK, or by setting the
 * global flag: window.__SC_DEBUG__ = true
 */

const PREFIX = "[SmartComply SDK]";

function isDebugEnabled(): boolean {
  try {
    return !!(window as any).__SC_DEBUG__;
  } catch {
    return false;
  }
}

export const SDKLogger = {
  /** Emit an informational message (debug mode only). */
  info(message: string, ...args: unknown[]): void {
    if (!isDebugEnabled()) return;
    console.info(`${PREFIX} ℹ ${message}`, ...args);
  },

  /**
   * Emit a low-noise informational message that's always visible, debug
   * mode or not — for the rare case (e.g. the SDK version on init) where
   * knowing this without having to flip on debug logging first matters
   * more than staying silent by default.
   */
  always(message: string, ...args: unknown[]): void {
    console.info(`${PREFIX} ${message}`, ...args);
  },

  /** Emit a warning (always visible). */
  warn(message: string, ...args: unknown[]): void {
    console.warn(`${PREFIX} ⚠ ${message}`, ...args);
  },

  /** Emit an error (always visible). */
  error(message: string, ...args: unknown[]): void {
    console.error(`${PREFIX} ✖ ${message}`, ...args);
  },

  /** Emit a debug trace (debug mode only — lowest priority). */
  debug(message: string, ...args: unknown[]): void {
    if (!isDebugEnabled()) return;
    console.debug(`${PREFIX} ⬡ ${message}`, ...args);
  },

  /** Log a timed group of related messages (debug mode only). */
  group(label: string, fn: () => void): void {
    if (!isDebugEnabled()) { fn(); return; }
    console.group(`${PREFIX} ${label}`);
    fn();
    console.groupEnd();
  },

  /**
   * Enable or disable verbose debug output at runtime.
   *
   * Example:
   *   import { SDKLogger } from "smartcomply-web-sdk";
   *   SDKLogger.setDebug(true);
   */
  setDebug(enabled: boolean): void {
    try {
      (window as any).__SC_DEBUG__ = enabled;
    } catch {
      /* no-op in non-browser environments */
    }
  },
};
